import { useState, useEffect, useCallback } from 'react';
import {
  institutionSearch,
  institutionUsers,
  institutionConfig,
  type SearchResult,
  type User,
  type DocumentType,
  type SecurityClassification,
  type DocumentStatus,
} from '@/services/institution';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import styles from './SearchPage.module.css';

export function SearchPage() {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  // Search params
  const [query, setQuery] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [classificationId, setClassificationId] = useState('');
  const [subjectUserId, setSubjectUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Reference data
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [statuses, setStatuses] = useState<DocumentStatus[]>([]);
  const [classifications, setClassifications] = useState<SecurityClassification[]>([]);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);

  // Suggestions
  const [suggestions, setSuggestions] = useState<{ id: string; title: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Advanced filters toggle
  const [showAdvanced, setShowAdvanced] = useState(false);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [typesRes, statusesRes, classRes, usersRes] = await Promise.all([
        institutionConfig.getDocumentTypes(),
        institutionConfig.getDocumentStatuses(),
        institutionConfig.getSecurityClassifications(),
        institutionUsers.list({ perPage: 100 }),
      ]);

      if (typesRes.success && typesRes.data) setDocumentTypes(typesRes.data);
      if (statusesRes.success && statusesRes.data) setStatuses(statusesRes.data);
      if (classRes.success && classRes.data) setClassifications(classRes.data);
      if (usersRes.success && usersRes.data) setStaffMembers(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch reference data:', err);
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  async function handleSearch(newPage = 1) {
    if (!query.trim() && !documentTypeId && !statusId && !classificationId && !subjectUserId && !dateFrom && !dateTo) {
      return;
    }

    setLoading(true);
    setSearched(true);
    setShowSuggestions(false);

    try {
      const response = await institutionSearch.search({
        query: query.trim() || '*',
        page: newPage,
        perPage: 20,
        documentTypeId: documentTypeId || undefined,
        statusId: statusId || undefined,
        securityClassificationId: classificationId || undefined,
        subjectUserId: subjectUserId || undefined,
        dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
        dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
      });

      if (response.success && response.data) {
        setResults(response.data);
        setPage(newPage);
        if (response.meta) {
          setTotalPages(response.meta.totalPages || 1);
          setTotalResults(response.meta.total || 0);
        }
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleQueryChange(value: string) {
    setQuery(value);

    if (value.length >= 2) {
      try {
        const response = await institutionSearch.getSuggestions(value);
        if (response.success && response.data) {
          setSuggestions(response.data);
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error('Failed to fetch suggestions:', err);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }

  function handleSuggestionClick(suggestion: { id: string; title: string }) {
    setQuery(suggestion.title);
    setShowSuggestions(false);
    handleSearch(1);
  }

  function clearFilters() {
    setQuery('');
    setDocumentTypeId('');
    setStatusId('');
    setClassificationId('');
    setSubjectUserId('');
    setDateFrom('');
    setDateTo('');
    setResults([]);
    setSearched(false);
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSearch(1);
    }
  }

  const hasActiveFilters = documentTypeId || statusId || classificationId || subjectUserId || dateFrom || dateTo;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Search Staff Records</h1>
        <p className={styles.subtitle}>Find records by title, reference number, or staff member</p>
      </div>

      <div className={styles.searchContainer}>
        <div className={styles.searchBox}>
          <div className={styles.searchInputWrapper}>
            <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by title, reference number, or description..."
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className={styles.searchInput}
            />
            {query && (
              <button className={styles.clearBtn} onClick={() => { setQuery(''); setSuggestions([]); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {showSuggestions && suggestions.length > 0 && (
            <div className={styles.suggestions}>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.id}
                  className={styles.suggestionItem}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                  </svg>
                  <span>{suggestion.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.searchActions}>
          <Button onClick={() => handleSearch(1)} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </Button>
          <button
            className={`${styles.advancedToggle} ${showAdvanced ? styles.active : ''}`}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="21" x2="4" y2="14" />
              <line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" />
              <line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" />
              <line x1="9" y1="8" x2="15" y2="8" />
              <line x1="17" y1="16" x2="23" y2="16" />
            </svg>
            Advanced Filters
            {hasActiveFilters && <span className={styles.filterBadge}></span>}
          </button>
        </div>

        {showAdvanced && (
          <div className={styles.advancedFilters}>
            <div className={styles.filterGrid}>
              <Select
                label="Record Type"
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
              >
                <option value="">All Types</option>
                {documentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Status"
                value={statusId}
                onChange={(e) => setStatusId(e.target.value)}
              >
                <option value="">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Classification"
                value={classificationId}
                onChange={(e) => setClassificationId(e.target.value)}
              >
                <option value="">All Classifications</option>
                {classifications.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Staff Member"
                value={subjectUserId}
                onChange={(e) => setSubjectUserId(e.target.value)}
              >
                <option value="">All Staff</option>
                {staffMembers.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.firstName} {staff.lastName}
                  </option>
                ))}
              </Select>

              <Input
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />

              <Input
                label="Date To"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            {hasActiveFilters && (
              <button className={styles.clearFiltersBtn} onClick={clearFilters}>
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {searched && (
        <div className={styles.resultsContainer}>
          <div className={styles.resultsHeader}>
            <span className={styles.resultsCount}>
              {totalResults} {totalResults === 1 ? 'result' : 'results'} found
            </span>
          </div>

          {loading ? (
            <div className={styles.loading}>Searching...</div>
          ) : results.length === 0 ? (
            <div className={styles.noResults}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
                <path d="M8 8l6 6M14 8l-6 6" />
              </svg>
              <p>No records found matching your search criteria</p>
              <Button variant="secondary" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <>
              <div className={styles.resultsList}>
                {results.map((result) => (
                  <div key={result.id} className={styles.resultItem}>
                    <div className={styles.resultIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                        <path d="M14 2V8H20" />
                      </svg>
                    </div>
                    <div className={styles.resultContent}>
                      <h3 className={styles.resultTitle}>{result.title}</h3>
                      <div className={styles.resultMeta}>
                        {result.referenceNumber && (
                          <span className={styles.resultRef}>{result.referenceNumber}</span>
                        )}
                        <span className={styles.resultType}>{result.typeName}</span>
                        <span className={styles.resultDate}>{formatDate(result.createdAt)}</span>
                      </div>
                      {result.description && (
                        <p className={styles.resultDescription}>
                          {result.description.length > 150
                            ? result.description.substring(0, 150) + '...'
                            : result.description}
                        </p>
                      )}
                    </div>
                    <div className={styles.resultBadges}>
                      <span className={styles.statusBadge}>{result.statusName}</span>
                      <span className={styles.classificationBadge}>{result.classificationName}</span>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSearch(page - 1)}
                    disabled={page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className={styles.pageInfo}>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSearch(page + 1)}
                    disabled={page === totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!searched && (
        <div className={styles.emptyState}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <h2>Search Staff Records</h2>
          <p>Enter a search term or use the advanced filters to find staff records</p>
          <div className={styles.searchTips}>
            <span className={styles.tipIcon}>💡</span>
            <span className={styles.tipText}>
              Try natural language: "Grade 14 officers", "staff retiring in 2025", or "incomplete files"
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
